import type { CrossSectionDataSet, SealDataContext } from "./d";
import {
  generateEquipmentDataPoints,
  generateCrossSectionDataSetTitle,
  generateMLEDataPoints
} from "./calculateCrossSectionDataSets";
import { convertSmallLengthUnits } from "@/libs/units/length";
import { getYToolBypassPositioning } from "./getYToolChartDataSet";

/**
 * Generates datasets representing the seal cross-sections
 *
 * @param context The cross-section chart context
 * @returns An array of datasets representing the seals
 */
export const getSealChartDataSet = (
  sealDataContext: SealDataContext
): CrossSectionDataSet[][] => {
  const sealChartSet = [];
  // Radius =$Inputs.F4/2, Center X, Center Y
  const casingInnerRadius = sealDataContext.casing.innerDiameterIn / 2;
  const casingInnerCenterX = 0;
  const casingInnerCenterY = 0;
  const casingInnerPoints = generateEquipmentDataPoints(
    casingInnerRadius,
    casingInnerCenterX,
    casingInnerCenterY
  );
  const casingDriftRadius = sealDataContext.casing.driftDiameterIn / 2;
  const casingDriftCenterX = 0;
  const casingDriftCenterY = 0;
  const casingDriftPoints = generateEquipmentDataPoints(
    casingDriftRadius,
    casingDriftCenterX,
    casingDriftCenterY
  );
  for (const seal of sealDataContext.seals) {
    const sealChartDataSet: CrossSectionDataSet[] = [];
    sealChartDataSet.push({
      title: generateCrossSectionDataSetTitle(
        "Casing ID",
        sealDataContext.casing.innerDiameterIn,
        sealDataContext.lengthUnit
      ),
      xAxisLabel: `${sealDataContext.lengthUnit.label}`,
      yAxisLabel: `${sealDataContext.lengthUnit.label}`,
      points: casingInnerPoints.map(pt => {
        return {
          x: convertSmallLengthUnits(pt.x, sealDataContext.lengthUnit),
          y: convertSmallLengthUnits(pt.y, sealDataContext.lengthUnit)
        };
      })
    });
    sealChartDataSet.push({
      title: generateCrossSectionDataSetTitle(
        "Casing Drift ID",
        sealDataContext.casing.driftDiameterIn,
        sealDataContext.lengthUnit
      ),
      xAxisLabel: `${sealDataContext.lengthUnit.label}`,
      yAxisLabel: `${sealDataContext.lengthUnit.label}`,
      points: casingDriftPoints.map(pt => {
        return {
          x: convertSmallLengthUnits(pt.x, sealDataContext.lengthUnit),
          y: convertSmallLengthUnits(pt.y, sealDataContext.lengthUnit)
        };
      })
    });
    // --- Seal Dim ---
    // Radius=$Inputs.D15/2, Center X, Center Y
    const sealDimRadius = seal.outerDiameterIn / 2;
    const maxEquipmentOuterDiameterIn =
      sealDataContext.maxEquipmentOuterDiameterIn || 0;
    let sealDimCenterX: number;
    const sealDimCenterY = 0;

    if (
      sealDataContext.hasYTool &&
      sealDataContext.bypassTubingOuterDiameterIn &&
      sealDataContext.yToolWidthIn
    ) {
      // Y-Tool Bypass Tubing Dim circle - use shared positioning logic
      const bypassPositioning = getYToolBypassPositioning({
        casingDriftRadius,
        yToolWidthIn: sealDataContext.yToolWidthIn,
        bypassTubingOuterDiameterIn:
          sealDataContext.bypassTubingOuterDiameterIn,
        lengthUnit: sealDataContext.lengthUnit
      });
      sealChartDataSet.push(bypassPositioning.bypassTubingDataSet);

      // Position seal using Excel formula: ='Y-Tool Cross'!K4
      // Max Equipment Center X = bypassCenterX + bypassRadius + maxEquipmentRadius
      const maxEquipmentDimRadius = maxEquipmentOuterDiameterIn / 2;
      sealDimCenterX =
        bypassPositioning.bypassTubingDimCenterX +
        bypassPositioning.bypassTubingDimRadius +
        maxEquipmentDimRadius;
    } else {
      // Standard positioning when no y-tool
      sealDimCenterX = -casingDriftRadius + maxEquipmentOuterDiameterIn / 2;
    }
    const sealDimPoints = generateEquipmentDataPoints(
      sealDimRadius,
      sealDimCenterX,
      sealDimCenterY
    );
    sealChartDataSet.push({
      title: generateCrossSectionDataSetTitle(
        "Seal OD",
        seal.outerDiameterIn,
        sealDataContext.lengthUnit
      ),
      xAxisLabel: `${sealDataContext.lengthUnit.label}`,
      yAxisLabel: `${sealDataContext.lengthUnit.label}`,
      points: sealDimPoints.map(pt => {
        return {
          x: convertSmallLengthUnits(pt.x, sealDataContext.lengthUnit),
          y: convertSmallLengthUnits(pt.y, sealDataContext.lengthUnit)
        };
      })
    });
    // Only chart MLE and Seal + MLE OD when there's no y-tool
    if (!sealDataContext.hasYTool) {
      // --- MLE Upper Dim ---
      // Radius =$Inputs.I13/2, (mle_depth_inches/2)
      const mleUpperDimRadius = sealDataContext.mle.depthIn / 2;
      // Center X =G3+K3+G4 (sealDimRadius + mleUpperDimRadius + sealDimCenterX)
      const mleUpperDimCenterX =
        sealDimRadius + mleUpperDimRadius + sealDimCenterX;
      // Center Y = =$Inputs.H13/2-$Inputs.I13/2 === mle.mle_width_inches/2 - mle_depth_inches/2
      const mleUpperDimCenterY =
        sealDataContext.mle.widthIn / 2 - sealDataContext.mle.depthIn / 2;
      // --- MLE Lower Dim ---
      // Radius == mleUpperDimRadius
      const mleLowerDimRadius = mleUpperDimRadius;
      // Center X same as upper
      const mleLowerDimCenterX = mleUpperDimCenterX;
      // Center Y = =-($Inputs.H13/2+$Inputs.I13/2) === -(motorData.mle_width_inches/2 - mle_depth_inches/2)
      const mleLowerDimCenterY = -(
        sealDataContext.mle.widthIn / 2 -
        sealDataContext.mle.depthIn / 2
      );
      const mleDimPoints = generateMLEDataPoints(
        mleUpperDimRadius,
        mleUpperDimCenterX,
        mleUpperDimCenterY,
        mleLowerDimRadius,
        mleLowerDimCenterX,
        mleLowerDimCenterY
      );
      sealChartDataSet.push({
        title: generateCrossSectionDataSetTitle(
          "MLE Depth",
          sealDataContext.mle.depthIn,
          sealDataContext.lengthUnit
        ),
        xAxisLabel: `${sealDataContext.lengthUnit.label}`,
        yAxisLabel: `${sealDataContext.lengthUnit.label}`,
        points: mleDimPoints.map(pt => {
          return {
            x: convertSmallLengthUnits(pt.x, sealDataContext.lengthUnit),
            y: convertSmallLengthUnits(pt.y, sealDataContext.lengthUnit)
          };
        })
      });
      // --- SEAL + MLE DIM ---
      // Radius =$Inputs.L15/2
      const maxCalculatedOuterDiameterIn =
        seal.maxCalculatedOuterDiameterIn || 0;
      const sealAndMleDimRadius = maxCalculatedOuterDiameterIn / 2;
      // Center X =Q3-G3+G4
      const sealAndMleDimCenterX =
        sealAndMleDimRadius - sealDimRadius + sealDimCenterX;
      const sealAndMleDimCenterY = 0;
      const sealAndMleDimPoints = generateEquipmentDataPoints(
        sealAndMleDimRadius,
        sealAndMleDimCenterX,
        sealAndMleDimCenterY
      );
      sealChartDataSet.push({
        title: generateCrossSectionDataSetTitle(
          "Seal + MLE OD",
          maxCalculatedOuterDiameterIn,
          sealDataContext.lengthUnit
        ),
        xAxisLabel: `${sealDataContext.lengthUnit.label}`,
        yAxisLabel: `${sealDataContext.lengthUnit.label}`,
        points: sealAndMleDimPoints.map(pt => {
          return {
            x: convertSmallLengthUnits(pt.x, sealDataContext.lengthUnit),
            y: convertSmallLengthUnits(pt.y, sealDataContext.lengthUnit)
          };
        })
      });
    }
    sealChartSet.push(sealChartDataSet);
  }
  return sealChartSet;
};
